/**
 * =============================================================================
 * COMO SE ORDENA Y SE PARTE LA LISTA DE TESTS DE UN PACIENTE
 * =============================================================================
 * Funciones puras, sin React y sin i18next, para poder comprobarlas sin montar
 * nada. Todo lo que decide QUE se ve y EN QUE ORDEN vive aqui; el componente
 * solo pinta.
 *
 * El endpoint devuelve por cada test: `title`, `count`, `firstDate`, `lastDate`,
 * `lastProcessedScore`, `subtype` y `clinicalArea`. Hasta ahora la interfaz
 * usaba dos de los siete —titulo y numero— y pintaba una hilera de botones
 * identicos. Los otros cinco son justo lo que convierte esa hilera en algo que
 * se puede leer.
 * =============================================================================
 */

/**
 * Orden de las areas clinicas.
 *
 * Fijo y no alfabetico. Alfabetico pondria "Cognitiva" antes que "Emocional"
 * por casualidad y "Fisica" en un sitio distinto segun si la tilde entra o no
 * en la comparacion. Un orden declarado se lee igual siempre y se discute
 * cambiandolo aqui.
 *
 * Las areas que no esten en esta lista NO se descartan: van detras, ordenadas
 * alfabeticamente. Si manana alguien crea un area nueva desde el panel, aparece
 * sola en su sitio en vez de desaparecer.
 */
export const ORDEN_DE_AREAS = [
  "Cognitiva",
  "Emocional",
  "Funcional",
  "Física",
  "General",
];

/**
 * Clave estable para agrupar, insensible a tildes y mayusculas.
 *
 * El rango va escrito con escapes y no con los caracteres combinantes en
 * crudo: son invisibles en un editor y cualquier copia, formateo o cambio de
 * codificacion los puede romper sin que se vea. Con `̀-ͯ` el rango
 * se lee y no se puede estropear.
 *
 * Hace falta porque el area llega escrita a mano desde el panel y "Física" y
 * "Fisica" tienen que caer en el mismo grupo.
 */
const TILDES = new RegExp("[\u0300-\u036f]", "g");

export function claveDeArea(area) {
  return String(area || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(TILDES, "");
}

const POSICION_POR_AREA = new Map(
  ORDEN_DE_AREAS.map((a, i) => [claveDeArea(a), i]),
);

/**
 * Parte el titulo en lo que nombra al test y lo que lo matiza.
 *
 *   "MEC de Lobo (Mini-Examen Cognoscitivo)"  ->  { "MEC de Lobo", "Mini-Examen Cognoscitivo" }
 *   "Índice de Barthel"                       ->  { "Índice de Barthel", null }
 *
 * NO SE TIRA EL PARENTESIS, se baja de jerarquia. Es la diferencia entre
 * ordenar la informacion y perderla: hay tests cuyo parentesis es lo unico que
 * los distingue —"(39 ítems)" frente a "(13 ítems SI/NO)"— y recortarlos
 * dejaria dos filas iguales que llevan a graficas distintas.
 *
 * Solo parte si el parentesis CIERRA el titulo. Uno en medio ("Escala (BBS) de
 * equilibrio") forma parte del nombre y se deja entero.
 */
export function partirTitulo(titulo) {
  const limpio = String(titulo || "").trim();
  if (!limpio) return { principal: "", matiz: null };

  const m = limpio.match(/^(.*?)\s*\(([^()]*)\)$/);
  if (!m) return { principal: limpio, matiz: null };

  const principal = m[1].trim();
  const matiz = m[2].trim();
  // Un titulo que es SOLO un parentesis se queda como esta: partirlo dejaria
  // la fila sin nombre.
  if (!principal) return { principal: limpio, matiz: null };
  return { principal, matiz: matiz || null };
}

/**
 * La puntuacion de la ultima medicion, lista para pintar.
 *
 * Devuelve null cuando no hay nada que ensenar, que NO es lo mismo que cero:
 * un test con `total: 0` de 15 se midio y salio cero, y esa es una informacion
 * clinica de primer orden. Confundir las dos cosas aqui pintaria un "—" donde
 * hay un resultado real.
 */
export function puntuacionDeUltima(lastProcessedScore) {
  const p = lastProcessedScore;
  if (!p || typeof p !== "object") return null;

  const total =
    typeof p.total === "number" && Number.isFinite(p.total) ? p.total : null;
  if (total === null) return null;

  const max =
    typeof p.maxPossible === "number" &&
    Number.isFinite(p.maxPossible) &&
    p.maxPossible > 0
      ? p.maxPossible
      : null;

  const interpretacion =
    typeof p.interpretation === "string" && p.interpretation.trim()
      ? p.interpretation.trim()
      : null;

  return { total, max, interpretacion };
}

/**
 * Agrupa el resumen por area clinica y ordena las dos cosas.
 *
 * Dentro de cada area, lo mas reciente primero: la pregunta con la que un
 * terapeuta abre esta pantalla es "que se le ha medido ultimamente", no "que
 * test empieza por A". Con la misma fecha, desempata el titulo para que el
 * orden no dependa de como devuelva Mongo los empates.
 *
 * @param {Array} resumen  lo que devuelve /evaluation-summary
 * @returns {Array<{area: string|null, clave: string, tests: Array}>}
 */
export function agruparPorArea(resumen) {
  const lista = Array.isArray(resumen) ? resumen : [];
  const grupos = new Map();

  for (const ev of lista) {
    if (!ev) continue;
    const area = ev.clinicalArea ? String(ev.clinicalArea).trim() : "";
    const clave = claveDeArea(area);
    if (!grupos.has(clave)) {
      grupos.set(clave, { area: area || null, clave, tests: [] });
    }
    grupos.get(clave).tests.push(ev);
  }

  for (const g of grupos.values()) {
    g.tests.sort((a, b) => {
      const fa = a.lastDate ? new Date(a.lastDate).getTime() : 0;
      const fb = b.lastDate ? new Date(b.lastDate).getTime() : 0;
      if (fb !== fa) return fb - fa;
      return String(a.title || "").localeCompare(String(b.title || ""));
    });
  }

  return [...grupos.values()].sort((a, b) => {
    const pa = POSICION_POR_AREA.has(a.clave)
      ? POSICION_POR_AREA.get(a.clave)
      : Number.MAX_SAFE_INTEGER;
    const pb = POSICION_POR_AREA.has(b.clave)
      ? POSICION_POR_AREA.get(b.clave)
      : Number.MAX_SAFE_INTEGER;
    if (pa !== pb) return pa - pb;
    // Las desconocidas, alfabeticamente. Las que no tienen area, al final del
    // todo: es un hueco de datos, no una categoria.
    if (!a.area) return 1;
    if (!b.area) return -1;
    return a.area.localeCompare(b.area);
  });
}
